import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { BrandingStateManagerService } from '../../../services/workspace/state-management/branding/branding-state-manager.service';
import { ColorPalletService } from '../../../services/color-pallet.service';
import { ColorsHelper } from 'lottie-json-helper/lib/helpers/colors-helper';

export interface IColorPicked {
    key: string | number;
    color: string;
}
@Component({
    selector: 'app-color-picker',
    templateUrl: './color-picker.component.html',
    styleUrl: './color-picker.component.scss',
})
export class ColorPickerComponent implements OnInit, OnDestroy {
    @Input({ alias: 'key', required: true }) key: string | number;
    @Input({ alias: 'color', required: true }) color: string;
    @Input({ alias: 'displayBrandColors', required: true }) displayBrandColors: boolean;
    @Output() colorChanged = new EventEmitter<IColorPicked>();
    private readonly numberOfRecentColorsLength = 7;
    recentlyUsedColors: string[] = [];
    brandPalette: string[] = [];
    onDestroy$ = new Subject<boolean>();
    chooseColorPickerKey: string | number | null = null;
    tempColor: string = '';

    constructor(
        private brandingStateManager: BrandingStateManagerService,
        private colorPlatteService: ColorPalletService
    ) {}

    ngOnInit(): void {
        this.brandingStateManager.branding$.pipe(takeUntil(this.onDestroy$)).subscribe((brandingState) => {
            const brandColors = brandingState?.colors;
            if (brandColors) {
                this.brandPalette = [
                    brandColors?.colorPrimary,
                    brandColors?.colorSecondary,
                    brandColors?.colorExtra,
                ].filter(Boolean);
            }
        });
        this.colorPlatteService.recentlyUsedColors$.pipe(takeUntil(this.onDestroy$)).subscribe((recentlyUsedColors) => {
            this.recentlyUsedColors = recentlyUsedColors.slice(-this.numberOfRecentColorsLength).reverse();
        });
    }
    selectColor(key: string | number, color: string) {
        this.colorChanged.emit({ key, color });
    }

    colorChosenFromPicker(key: string | number, color: string) {
        // convert rgba to hex if needed
        if (color.startsWith('rgb')) {
            color = this.rgbaToHex(color);
        }

        this.colorPlatteService.addColor(color);
        this.chooseColorPickerKey = null;
    }
    openColorPicker(key: string | number) {
        this.chooseColorPickerKey = key;
        this.tempColor = this.color;
    }
    cancelColor() {
        this.chooseColorPickerKey = null;
    }
    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    private rgbaToHex(rgba: string): string {
        const match = rgba.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+),\s*(\d+)\)$/);
        if (!match) return rgba; // return original if not valid rgba

        const r = Math.min(255, parseInt(match[1], 10));
        const g = Math.min(255, parseInt(match[2], 10));
        const b = Math.min(255, parseInt(match[3], 10));
        const a = Math.min(255, parseInt(match[4], 10)); // clamp alpha 0–255

        const toHex = (num: number) => num.toString(16).padStart(2, '0');

        return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(a)}`;
    }
}
